export async function useCmsPage(
    type: "docs" | "blog" | "pages" | "authorized-channel-insights",
    rootPath: string,
    navigationFile: string,
    paginationFile?: string
) {
    const route = useRoute();
    const { locale } = useI18n();
    const localePath = useLocalePath();

    const event = useRequestEvent();
    const root = localePath(rootPath);
    const documentPath = getCMSDocumentPath(type, locale.value, route.params.path);
    const navigationPath = getCMSDocumentPath(type, locale.value, navigationFile);
    const paginationPath = paginationFile ? getCMSDocumentPath(type, locale.value, paginationFile) : undefined;

    const documentFetch = useFetch<CMSDocument>(documentPath);
    const navigationFetch = useFetch<CMSNavItem[]>(navigationPath);
    const paginationFetch = paginationPath ? useFetch<CMSPostPagination>(paginationPath) : null;

    // useSeoMeta must be called synchronously (before any await) so that Nuxt's
    // instance context is still active. Computed refs are used so the values
    // update reactively once the final document is determined (including English fallback).
    const document = shallowRef<CMSDocument | null>(null);
    const seoTitle = computed(() => document.value?.title);
    const seoDescription = computed(() => document.value?.metaDescription ?? document.value?.description);
    const seoImage = computed(() => document.value?.image);

    useSeoMeta({
        title: seoTitle,
        ogTitle: seoTitle,
        description: seoDescription,
        ogDescription: seoDescription,
        ogImage: seoImage,
    });

    await Promise.all([documentFetch, navigationFetch, paginationFetch ?? Promise.resolve(null)]);

    document.value = documentFetch.data.value ?? null;
    const notFound = ref(false);

    if (documentFetch.error.value?.statusCode === 404) {
        // For 404s, try fetching the English version as a fallback before giving up.
        // $fetch is used here (rather than useFetch) because this runs after an await,
        // where the Nuxt composable context is no longer available.
        if (locale.value !== "en") {
            const englishPath = getCMSDocumentPath(type, "en", route.params.path);
            try {
                const fallback = await $fetch<CMSDocument>(englishPath);
                // Strip prev/next links — they point to English-locale URLs and would
                // take the user out of their current language unexpectedly.
                document.value = { ...fallback, previousLink: undefined, nextLink: undefined };
            } catch (error: unknown) {
                // $fetch (ofetch) puts the HTTP status on error.response.status.
                // Only swallow genuine 404s; rethrow anything else (5xx, network, etc.).
                const status = (error as { response?: { status?: number } }).response?.status;
                if (status !== 404) throw showError(error as Error);
                // English fallback also returned 404; fall through to notFound below.
            }
        }

        if (!document.value) {
            notFound.value = true;
            // Set 404 on SSR responses so search engines do not index missing pages.
            if (event) setResponseStatus(event, 404);
        }
    } else if (documentFetch.error.value) {
        throw showError(documentFetch.error.value);
    }

    const navigation = navigationFetch.data;
    const pagination = paginationFetch?.data ?? ref<CMSPostPagination | null>(null);

    const isRoot = computed(() => !route.params.path || route.params.path[0] === "page");

    const isTag = computed(() => !!(route.params.path && route.params.path[0] === "tag"));

    const currentTag = computed(() =>
        isTag.value && pagination.value ? pagination.value.tags.find((t) => route.path.startsWith(t.path)) : null
    );

    const pages = computed(() => {
        if (!pagination.value) return undefined;
        if (isRoot.value) return pagination.value.pages;
        return currentTag.value?.pages;
    });

    return { document, navigation, pagination, pages, root, notFound };
}
